import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ProductService } from 'src/app/services/product.service';
import { SeasonService } from 'src/app/services/season.service';
import { ApiBusinessService } from 'src/app/services/api-business.service';
import { InfoCardComponent } from '../../../shared/info-card/info-card.component';

@Component({
  selector: 'app-products-list',
  templateUrl: './products-list.component.html',
  styleUrls: ['./products-list.component.scss']
})
export class ProductsListComponent implements OnInit {

  business: any;
  products: any[] = [];
  seasons: any[] = [];
  loaded: boolean = false;
  loadingPage: boolean = true;

  searchText: string = '';
  seasonFilter: number | '' = '';
  seasonDropdownOpen: boolean = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-group')) {
      this.seasonDropdownOpen = false;
    }
  }

  private searchTerms = new Subject<string>();

  displayedColumns: string[] = ['actions', 'name', 'price', 'seasons'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  @ViewChild(MatSort) sort: MatSort = new MatSort();

  constructor(
    private router: Router,
    private productService: ProductService,
    private seasonService: SeasonService,
    private apiBusiness: ApiBusinessService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void { }

  ngAfterViewInit() {
    this.apiBusiness.changed.subscribe({
      next: (value) => {
        this.business = JSON.parse(localStorage.getItem('business') || '{}');
        if (value) {
          this.loadSeasons();
        }
      }
    });

    this.searchTerms.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchText = term;
      this.loadProducts();
    });
  }

  loadSeasons() {
    const companyId = this.business?.id;
    if (!companyId) {
      this.loadProducts();
      return;
    }
    this.seasonService.getSeasonsByCompany(companyId).subscribe({
      next: (res) => {
        this.seasons = res.data || [];
        this.loadProducts();
      },
      error: () => {
        this.seasons = [];
        this.loadProducts();
      }
    });
  }

  loadProducts() {
    this.loadingPage = true;

    if (!this.business?.id) {
      this.products = [];
      this.dataSource = new MatTableDataSource<any>([]);
      this.loaded = true;
      this.loadingPage = false;
      return;
    }

    const filters: any = {};
    if (this.searchText) filters.search = this.searchText;
    if (this.seasonFilter) filters.id_season = this.seasonFilter;

    this.productService.getProducts(this.business.id, filters).subscribe({
      next: (res) => {
        this.products = res.data || [];
        this.dataSource = new MatTableDataSource(this.products);
        this.dataSource.sort = this.sort;
        this.loaded = true;
        this.loadingPage = false;
      },
      error: () => {
        this.products = [];
        this.dataSource = new MatTableDataSource<any>([]);
        this.loaded = true;
        this.loadingPage = false;
      }
    });
  }

  onSearch(value: string) {
    this.searchTerms.next(value);
  }

  selectSeasonFilter(seasonId: number | '') {
    this.seasonFilter = seasonId;
    this.seasonDropdownOpen = false;
    this.loadProducts();
  }

  get seasonFilterLabel(): string {
    if (this.seasonFilter === '') return 'Temporada';
    const season = this.seasons.find(s => s.id === this.seasonFilter);
    return season ? season.name : 'Temporada';
  }

  clearFilters() {
    this.searchText = '';
    this.seasonFilter = '';
    this.loadProducts();
  }

  // Un producto puede tener varias asignaciones en la misma temporada (una por
  // categoría y nivel de butaca), así que la temporada se muestra una sola vez.
  getSeasonNames(product: any): string[] {
    return [...new Set<string>((product.seasons || []).map((s: any) => s.name))];
  }

  openCreate() {
    this.router.navigate(['/panel-control/sports-passes/products/create']);
  }

  openEdit(product: any) {
    this.router.navigate(['/panel-control/sports-passes/products/edit', product.id]);
  }

  deleteProduct(product: any) {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      disableClose: true,
      data: { icon: true, text: '¿Estás seguro de que quieres eliminar este producto? Esta acción no podrá deshacerse.', btnCancel: true, warning: true }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.productService.deleteProduct(this.business.id, product.id).subscribe({
          next: () => {
            this.openInfoCard('Producto eliminado correctamente');
            this.loadProducts();
          },
          error: (err) => {
            this.openInfoCard(this.extractErrorMessage(err, 'Error al eliminar el producto'));
          }
        });
      }
    });
  }

  private extractErrorMessage(err: any, fallback: string): string {
    if (err?.status === 422 && err?.error?.errors && typeof err.error.errors === 'object' && !Array.isArray(err.error.errors)) {
      const first: any = Object.values(err.error.errors)[0];
      if (Array.isArray(first) && first.length) {
        return first[0];
      }
    }
    return err?.error?.message || fallback;
  }

  private openInfoCard(msg: string) {
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { text: msg }
    });
  }
}
